import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Grid, Chip, Stack, LinearProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Divider, Tab, Tabs,
  Table, TableHead, TableRow, TableCell,
  IconButton, InputAdornment, Rating,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import RateReviewIcon from '@mui/icons-material/RateReview';

import {
  VENUES_QUERY,
  MY_VENUE_BOOKINGS_QUERY,
  BOOK_VENUE_MUTATION,
  CANCEL_VENUE_BOOKING_MUTATION,
  MY_CEREMONIES_QUERY,
  VENUE_REVIEWS_QUERY,
  ADD_VENUE_REVIEW_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLORS = { pending: 'warning', confirmed: 'success', cancelled: 'error' };

// ── Venue Reviews Dialog ─────────────────────────────────────
function VenueReviewsDialog({ venue, open, onClose }) {
  const [rating, setRating]         = useState(0);
  const [reviewText, setReviewText] = useState('');
  const { data, refetch } = useQuery(VENUE_REVIEWS_QUERY, { variables: { venueId: venue?.id }, skip: !venue?.id });
  const [addReview] = useMutation(ADD_VENUE_REVIEW_MUTATION, {
    onCompleted: () => { setRating(0); setReviewText(''); refetch(); },
  });
  const reviews = data?.venueReviews || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reviews — {venue?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Write a Review</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2">Rating:</Typography>
              <Rating value={rating} onChange={(_, v) => setRating(v)} />
            </Stack>
            <TextField label="Your review" value={reviewText} onChange={e => setReviewText(e.target.value)}
              fullWidth multiline rows={2} size="small" />
            <Button variant="contained" size="small" sx={{ mt: 1 }}
              onClick={() => addReview({ variables: { venueId: venue?.id, rating, reviewText: reviewText || null } })}
              disabled={!rating}>Submit</Button>
          </Box>
          {reviews.length === 0 && (
            <Typography variant="body2" color="text.secondary">No reviews yet. Be the first!</Typography>
          )}
          {reviews.map(r => (
            <Box key={r.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Rating value={r.rating} readOnly size="small" />
                <Typography variant="caption" color="text.secondary">
                  {r.reviewer?.email} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : ''}
                </Typography>
              </Stack>
              {r.reviewText && <Typography variant="body2" sx={{ mt: 0.5 }}>{r.reviewText}</Typography>}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function VenueCard({ venue, onBook }) {
  const [reviewOpen, setReviewOpen] = useState(false);
  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>{venue.name}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <LocationOnIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{venue.city}{venue.state ? `, ${venue.state}` : ''}</Typography>
          </Stack>
          {venue.venueType && (
            <Chip label={venue.venueType.replace('_', ' ')} size="small" variant="outlined" color="primary" sx={{ mb: 1 }} />
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            {(venue.capacityMin || venue.capacityMax) && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2">{venue.capacityMin || 0}–{venue.capacityMax || '∞'} guests</Typography>
              </Stack>
            )}
            {venue.pricePerDay && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AttachMoneyIcon fontSize="small" color="action" />
                <Typography variant="body2">₹{Number(venue.pricePerDay).toLocaleString('en-IN')}/day</Typography>
              </Stack>
            )}
          </Stack>
          {venue.facilities?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {venue.facilities.slice(0, 4).map(f => (
                <Chip key={f} label={f} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
              {venue.facilities.length > 4 && <Chip label={`+${venue.facilities.length - 4}`} size="small" />}
            </Box>
          )}
          {venue.contactPhone && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{venue.contactPhone}</Typography>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <StarIcon fontSize="small" sx={{ color: 'gold' }} />
            <Typography variant="body2">
              {venue.averageRating ? Number(venue.averageRating).toFixed(1) : 'No rating'} · {venue.reviewCount || 0} review{venue.reviewCount !== 1 ? 's' : ''}
            </Typography>
          </Stack>
          {venue.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{venue.notes}</Typography>
          )}
        </CardContent>
        <CardActions>
          <Button size="small" variant="contained" startIcon={<BookmarkAddIcon />} onClick={() => onBook(venue)}>
            Book Venue
          </Button>
          <Button size="small" startIcon={<RateReviewIcon />} onClick={() => setReviewOpen(true)}>
            Reviews {venue.reviewCount ? `(${venue.reviewCount})` : ''}
          </Button>
          {venue.contactEmail && (
            <Button size="small" href={`mailto:${venue.contactEmail}`} target="_blank">Contact</Button>
          )}
        </CardActions>
      </Card>
      <VenueReviewsDialog venue={venue} open={reviewOpen} onClose={() => setReviewOpen(false)} />
    </>
  );
}

export default function VenuesPage() {
  const [tab, setTab] = useState(0);
  const [filters, setFilters]   = useState({ city: '', venueType: '', minCapacity: '', maxCapacity: '' });
  const [search, setSearch]     = useState({ city: '', venueType: '', minCapacity: '', maxCapacity: '' });
  const [bookVenue, setBookVenue] = useState(null);
  const [bookForm, setBookForm]  = useState({ ceremonyId: '', eventDate: '', slot: 'full_day', guestCount: '', specialRequests: '', notes: '' });

  const { options: venueTypeOpts } = useLov('venue_type');
  const { options: slotOpts }      = useLov('venue_slot');

  const { data: venueData, loading: venueLoading, error: venueError } = useQuery(VENUES_QUERY, {
    variables: { city: search.city || undefined, venueType: search.venueType || undefined,
      minCapacity: search.minCapacity ? parseInt(search.minCapacity) : undefined,
      maxCapacity: search.maxCapacity ? parseInt(search.maxCapacity) : undefined },
    fetchPolicy: 'cache-and-network',
  });
  const { data: bookingsData, loading: bookingLoading, refetch: refetchBookings } = useQuery(MY_VENUE_BOOKINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY);

  const [doBookVenue]  = useMutation(BOOK_VENUE_MUTATION,  { onCompleted: () => { setBookVenue(null); refetchBookings(); } });
  const [cancelBooking] = useMutation(CANCEL_VENUE_BOOKING_MUTATION, { onCompleted: refetchBookings });

  const venues   = venueData?.venues || [];
  const bookings = bookingsData?.myVenueBookings || [];
  const cerems   = ceremonyData?.myCeremonies || [];

  const handleBook = () => {
    doBookVenue({
      variables: {
        venueId: bookVenue.id,
        ceremonyId:      bookForm.ceremonyId     || null,
        eventDate:       bookForm.eventDate,
        slot:            bookForm.slot,
        guestCount:      bookForm.guestCount      ? parseInt(bookForm.guestCount)  : null,
        specialRequests: bookForm.specialRequests || null,
        notes:           bookForm.notes           || null,
      }
    });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>Venues</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Discover and book wedding halls and banquet venues</Typography>

      <ContractedOfferingsPanel categories={['venue']} title="Contracted venues" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Browse Venues" />
        <Tab label={`My Bookings (${bookings.length})`} />
      </Tabs>

      {/* Browse Tab */}
      {tab === 0 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField label="City" value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} fullWidth size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select label="Venue Type" value={filters.venueType} onChange={e => setFilters(f => ({ ...f, venueType: e.target.value }))} fullWidth size="small">
                  <MenuItem value="">All Types</MenuItem>
                  {venueTypeOpts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Min Capacity" type="number" value={filters.minCapacity} onChange={e => setFilters(f => ({ ...f, minCapacity: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Max Capacity" type="number" value={filters.maxCapacity} onChange={e => setFilters(f => ({ ...f, maxCapacity: e.target.value }))} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button variant="contained" fullWidth onClick={() => setSearch({ ...filters })}>Search</Button>
              </Grid>
            </Grid>
          </Card>

          {venueLoading && <LinearProgress />}
          {venueError && <Alert severity="error" sx={{ mb: 2 }}>{venueError.message}</Alert>}

          {!venueLoading && venues.length === 0 && (
            <Alert severity="info">No venues found matching your criteria. Ask an admin to add venues.</Alert>
          )}

          <Grid container spacing={2}>
            <Paged items={venues}>{rows => rows.map(v => (
              <Grid item xs={12} sm={6} md={4} key={v.id}>
                <VenueCard venue={v} onBook={setBookVenue} />
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* My Bookings Tab */}
      {tab === 1 && (
        <Box>
          {bookingLoading && <LinearProgress />}
          {bookings.length === 0 && !bookingLoading && (
            <Alert severity="info">No venue bookings yet. Browse venues and make a booking.</Alert>
          )}
          {bookings.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell>Ceremony</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Guests</TableCell>
                  <TableCell>Cost (₹)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map(b => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{b.venue?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{b.venue?.city}</Typography>
                    </TableCell>
                    <TableCell>{b.ceremony?.name || '—'}</TableCell>
                    <TableCell>{b.eventDate ? new Date(b.eventDate).toLocaleDateString('en-IN') : '—'}</TableCell>
                    <TableCell>{b.slot}</TableCell>
                    <TableCell>{b.guestCount || '—'}</TableCell>
                    <TableCell>{b.totalCost ? `₹${Number(b.totalCost).toLocaleString('en-IN')}` : '—'}</TableCell>
                    <TableCell><Chip label={b.status} size="small" color={STATUS_COLORS[b.status] || 'default'} /></TableCell>
                    <TableCell>
                      {b.status !== 'cancelled' && (
                        <IconButton size="small" color="error" title="Cancel Booking"
                          onClick={() => window.confirm('Cancel this venue booking?') && cancelBooking({ variables: { id: b.id } })}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Book Dialog */}
      <Dialog open={Boolean(bookVenue)} onClose={() => setBookVenue(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Book — {bookVenue?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {cerems.length > 0 && (
              <TextField select label="Link to Ceremony (optional)" value={bookForm.ceremonyId}
                onChange={e => setBookForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">— No ceremony —</MenuItem>
                {cerems.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Event Date" type="date" value={bookForm.eventDate}
              onChange={e => setBookForm(f => ({ ...f, eventDate: e.target.value }))} fullWidth required InputLabelProps={{ shrink: true }} />
            <TextField select label="Slot" value={bookForm.slot}
              onChange={e => setBookForm(f => ({ ...f, slot: e.target.value }))} fullWidth>
              {slotOpts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField label="Expected Guest Count" type="number" value={bookForm.guestCount}
              onChange={e => setBookForm(f => ({ ...f, guestCount: e.target.value }))} fullWidth />
            <TextField label="Special Requests" value={bookForm.specialRequests}
              onChange={e => setBookForm(f => ({ ...f, specialRequests: e.target.value }))} fullWidth multiline rows={2} />
            <TextField label="Notes" value={bookForm.notes}
              onChange={e => setBookForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookVenue(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleBook} disabled={!bookForm.eventDate}>Confirm Booking Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
